import type {
  BusinessStatus,
  ConflictTypeLegacy as ConflictType,
  RuntimeStatus,
  SourceConflictLegacy as SourceConflict,
} from '@staiter/shared';
import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { EmptyState, EntityRefCell, ErrorState, Skeleton, Table } from '~/components/data';
import {
  AIInsightCard,
  ApprovalDiff,
  BatchPicker,
  ConflictCard,
  HeroMetric,
  ItemPicker,
  ManufacturerPicker,
  UserPicker,
  WarehousePicker,
} from '~/components/domain';
import { ToastProvider, useToast } from '~/components/feedback';
import {
  Button,
  DatePicker,
  DateRangePicker,
  DateTimePicker,
  type IsoDateRange,
  Tooltip,
} from '~/components/primitives';
import { ProvenanceChip, RuntimeStatusPill, StatusBadge } from '~/components/status';
import styles from './_auth.design-system.module.css';

// S4 dev-only route — storybook-lite визуальный обзор всех primitive'ов.
// TODO(S12): вынести в полноценный Storybook или interactive docs.

export const Route = createFileRoute('/_auth/design-system')({
  component: DesignSystemPage,
});

const RUNTIME_STATUSES: RuntimeStatus[] = [
  'queued',
  'running',
  'waiting_approval',
  'waiting_external',
  'failed',
  'completed',
  'cancelled',
];

const BUSINESS_STATUSES: BusinessStatus[] = [
  'completed_draft',
  'needs_review',
  'degraded',
  'blocked',
  'escalated',
];

const CONFLICT_TYPES: ConflictType[] = [
  'intra_level',
  'master_data_drift',
  'bom_explosion_loop',
  'valuation_mismatch',
  'temporal_precedence_unresolved',
  'reconciliation_variance',
  'tolerance_exceeded',
  'manual_override_deferred',
];

function makeConflict(type: ConflictType): SourceConflict {
  return {
    id: `c-${type}`,
    type,
    severity: 'high',
    subject: `Пример: ${type} — SKU M-4040 / склад A-01`,
    source_a: {
      source: 'staiter',
      level: 'L1',
      value: '124.50 м²',
      document_id: 'SE-2026-0412',
      fetched_at: new Date().toISOString(),
    },
    source_b: {
      source: '1c',
      level: 'L5',
      value: '128.00 м²',
      document_id: '4521',
      fetched_at: new Date().toISOString(),
    },
    authoritative: 'a',
    diff_label: '+3.50 м² (2.8%)',
  };
}

function DesignSystemPage(): JSX.Element {
  return (
    <ToastProvider>
      <main className={styles.page}>
        <header>
          <h1>Design System — S4 primitives</h1>
          <p className={styles.lede}>
            Dev-обзор компонентов. Не production-страница. Отражает STAITER_UI §4.
          </p>
        </header>

        <Section title="RuntimeStatusPill (7 состояний)">
          <div className={styles.row}>
            {RUNTIME_STATUSES.map((s) => (
              <RuntimeStatusPill key={s} runtime_status={s} />
            ))}
          </div>
        </Section>

        <Section title="StatusBadge — business status (5)">
          <div className={styles.row}>
            {BUSINESS_STATUSES.map((s) => (
              <StatusBadge key={s} status={s} />
            ))}
          </div>
        </Section>

        <Section title="ProvenanceChip">
          <div className={styles.row}>
            <ProvenanceChip
              source="staiter"
              document_id="SE-2026-0412"
              fetched_at={new Date().toISOString()}
            />
            <ProvenanceChip
              source="1c"
              document_id="4521"
              fetched_at={new Date(Date.now() - 3600_000).toISOString()}
              onOpenEvidence={() => alert('open evidence')}
            />
            <ProvenanceChip source="bitrix" document_id="DEAL-9912" />
            <ProvenanceChip source="adesk" />
            <ProvenanceChip source="excel" document_id="manual-override" />
          </div>
        </Section>

        <Section title="HeroMetric (max 3 на экране)">
          <div className={styles.grid3}>
            <HeroMetric
              label="Нераспределённых списаний"
              value={{ amount: '124580', currency: 'KZT' }}
              delta={-8.3}
              deltaLabel="vs пред. период"
              hint="4 open · 2 blocked"
            />
            <HeroMetric label="Runs сегодня" value={42} delta={12.5} trend="up" />
            <HeroMetric label="Anomalies inbox" value={7} trend="flat" hint="3 high · 4 medium" />
          </div>
        </Section>

        <Section title="AIInsightCard">
          <AIInsightCard
            title="AI-интерпретация"
            trace_id="r-2026-04-13-8f2"
            model="model-id"
            confidence={0.82}
            evidence_refs={[
              { id: 'e1', source: '1c', document_id: '4521', fetched_at: new Date().toISOString() },
              { id: 'e2', source: 'staiter', document_id: 'WO-0031' },
            ]}
          >
            Списание 18 шт раздражителя на заказ #3421 согласно BOM, но фактический yield 94% вместо
            нормативного 96%. Возможная причина — партия-поставка DB-22 с нестандартной вязкостью.
          </AIInsightCard>
        </Section>

        <Section title="ConflictCard — 8 taxonomy types">
          <div className={styles.grid1}>
            {CONFLICT_TYPES.map((t) => (
              <ConflictCard key={t} conflict={makeConflict(t)} onAction={(a) => console.log(a)} />
            ))}
          </div>
        </Section>

        <Section title="ApprovalDiff">
          <ApprovalDiff
            before={{ qty: '124.50', warehouse: 'A-01', batch: 'DB-22' }}
            after={{ qty: '128.00', warehouse: 'A-01', batch: 'DB-23' }}
            labels={{ qty: 'Количество', warehouse: 'Склад', batch: 'Партия' }}
          />
        </Section>

        <Section title="Table">
          <Table
            ariaLabel="Пример таблицы"
            data={[
              { id: '1', sku: 'M-4040', qty: 124.5, warehouse: 'A-01' },
              { id: '2', sku: 'M-4041', qty: 98.25, warehouse: 'A-02' },
              { id: '3', sku: 'M-4042', qty: 0, warehouse: 'B-01' },
            ]}
            rowKey={(r) => r.id}
            showDensityToggle
            stickyFirstColumn
            columns={[
              {
                id: 'sku',
                header: 'SKU',
                accessor: (r) => r.sku,
                sortValue: (r) => r.sku,
                sortable: true,
              },
              {
                id: 'qty',
                header: 'Кол-во',
                accessor: (r) => r.qty.toFixed(2),
                align: 'right',
                mono: true,
                sortValue: (r) => r.qty,
              },
              { id: 'wh', header: 'Склад', accessor: (r) => r.warehouse },
            ]}
          />
        </Section>

        <Section title="EmptyState + Skeleton">
          <div className={styles.grid2}>
            <EmptyState title="Пока пусто" description="Создайте первую запись, чтобы начать." />
            <div>
              <Skeleton height={24} />
              <div className={styles.spacer2} />
              <Skeleton width="60%" />
              <div className={styles.spacer2} />
              <Skeleton width="80%" />
            </div>
          </div>
        </Section>

        <Section title="ErrorState (F7)">
          <ErrorState error={new Error('Пример ошибки для демо')} onRetry={() => {}} />
        </Section>

        <Section title="EntityRefCell (F20) — name + muted code">
          <Table
            ariaLabel="EntityRefCell demo"
            data={[
              { item_name: 'Мембранное полотно BW-8040-LP440D', item_sku: 'ME-00000000054' },
              { item_name: null, item_sku: 'ME-00000000079' },
            ]}
            rowKey={(_r, i) => String(i)}
            columns={[
              {
                id: 'item',
                header: 'Позиция',
                accessor: (r) => <EntityRefCell name={r.item_name} code={r.item_sku} />,
              },
            ]}
          />
        </Section>

        <Section title="Pickers (F1) — ItemPicker / WarehousePicker / UserPicker / BatchPicker / ManufacturerPicker">
          <PickerShowcase />
        </Section>

        <Section title="Date primitives (F2) — DatePicker / DateTimePicker / DateRangePicker">
          <DateShowcase />
        </Section>

        <Section title="Tooltip + Toast">
          <div className={styles.row}>
            <Tooltip content="Примечание для screen reader">
              <Button variant="secondary">Наведи для tooltip</Button>
            </Tooltip>
            <ToastTrigger />
          </div>
        </Section>
      </main>
    </ToastProvider>
  );
}

function PickerShowcase(): JSX.Element {
  const [item, setItem] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [user, setUser] = useState('');
  const [batch, setBatch] = useState('');
  const [mfr, setMfr] = useState('');
  return (
    <div className={styles.grid2}>
      <ItemPicker label="Позиция" value={item} onChange={setItem} />
      <WarehousePicker label="Склад" value={warehouse} onChange={setWarehouse} />
      <UserPicker label="Пользователь" value={user} onChange={setUser} />
      <BatchPicker label="Партия" value={batch} onChange={setBatch} />
      <ManufacturerPicker label="Производитель" value={mfr} onChange={setMfr} />
    </div>
  );
}

function DateShowcase(): JSX.Element {
  const [d, setD] = useState<string | null>(null);
  const [dt, setDt] = useState<string | null>(null);
  const [range, setRange] = useState<IsoDateRange>({ from: null, to: null });
  return (
    <div className={styles.grid2}>
      <DatePicker label="Дата" value={d} onChange={setD} />
      <DateTimePicker label="Дата и время" value={dt} onChange={setDt} />
      <DateRangePicker label="Период" value={range} onChange={setRange} />
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <section className={styles.section}>
      <h2 className={styles.h2}>{title}</h2>
      {children}
    </section>
  );
}

function ToastTrigger(): JSX.Element {
  const { push } = useToast();
  return (
    <div className={styles.row}>
      <Button
        onClick={() =>
          push({ variant: 'info', title: 'Info', description: 'Нейтральное сообщение.' })
        }
      >
        Info
      </Button>
      <Button
        variant="secondary"
        onClick={() =>
          push({ variant: 'success', title: 'Готово', description: 'Операция выполнена.' })
        }
      >
        Success
      </Button>
      <Button
        variant="secondary"
        onClick={() =>
          push({ variant: 'warning', title: 'Внимание', description: 'Проверьте данные.' })
        }
      >
        Warning
      </Button>
      <Button
        variant="destructive"
        onClick={() =>
          push({ variant: 'error', title: 'Ошибка', description: 'Что-то пошло не так.' })
        }
      >
        Error
      </Button>
    </div>
  );
}
